import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors } from '../styles/colors';

interface InfoTooltipProps {
  text: React.ReactNode;
  // Nome da métrica, lido por leitor de tela no botão
  label?: string;
}

interface TooltipPosition {
  top: number;
  left: number;
  width: number;
  above: boolean;
}

const MAX_WIDTH = 300;
const GAP = 8;
const MARGIN = 8;

let tooltipCount = 0;

// Posição fixa calculada a partir do botão: tooltip absoluto seria cortado pelas
// tabelas com rolagem horizontal da tela.
const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, label = 'Mais informações' }) => {
  const idRef = useRef(`info-tooltip-${++tooltipCount}`);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const open = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.min(MAX_WIDTH, window.innerWidth - MARGIN * 2);
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - width / 2, MARGIN),
      window.innerWidth - width - MARGIN
    );
    // pouco espaço embaixo do botão: abre para cima
    const above = window.innerHeight - rect.bottom < 180 && rect.top > 180;

    setPosition({ top: above ? rect.top - GAP : rect.bottom + GAP, left, width, above });
  }, []);

  const close = useCallback(() => setPosition(null), []);

  useEffect(() => {
    if (!position) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    window.addEventListener('keydown', onKeyDown);
    // posição é fixa: rolar ou redimensionar deixaria o balão solto no lugar errado
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [position, close]);

  return (
    <span style={styles.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={position ? idRef.current : undefined}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        onClick={(event) => {
          event.stopPropagation();
          open();
        }}
        style={styles.trigger}
      >
        i
      </button>
      {position && (
        <span
          id={idRef.current}
          role="tooltip"
          style={{
            ...styles.tooltip,
            top: position.top,
            left: position.left,
            width: position.width,
            transform: position.above ? 'translateY(-100%)' : undefined,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '0.3rem',
    verticalAlign: 'middle',
  },
  trigger: {
    width: '15px',
    height: '15px',
    padding: 0,
    borderRadius: '50%',
    border: `1px solid ${colors.textMuted}`,
    backgroundColor: 'transparent',
    color: colors.textLight,
    fontSize: '10px',
    fontWeight: 700,
    fontFamily: 'Georgia, serif',
    fontStyle: 'italic',
    lineHeight: '13px',
    textAlign: 'center',
    cursor: 'help',
  },
  tooltip: {
    position: 'fixed',
    zIndex: 2000,
    padding: '0.6rem 0.75rem',
    backgroundColor: '#212121',
    color: colors.white,
    borderRadius: '6px',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
    fontSize: '0.78rem',
    fontWeight: 400,
    fontStyle: 'normal',
    lineHeight: 1.45,
    textAlign: 'left',
    whiteSpace: 'normal',
    textTransform: 'none',
    letterSpacing: 'normal',
    pointerEvents: 'none',
  },
};

export default InfoTooltip;
